import React, { useState } from "react";
import { HiChevronRight, HiFolderOpen, HiOutlineTrash } from "react-icons/hi";
import { BiPlus } from "react-icons/bi";

import { CustomButton } from "./CustomButton";
import UnlockBadge from "./UnlockBadge";
import { PlanTier, usePermissions } from "../../utils/permissions";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import UpgradeModal from "../modals/UpgradeModal";
import { PremiumBenefit } from "./UpgradePromptContent";
import { FaLock } from "react-icons/fa6";
import NoTranslate from "./NoTranslate";
import { SpaceEmptyState } from "./SpaceEmptyState";

export const RESERVED_FOLDER_KEYS = ["e-signature"];

interface FolderGridProps {
  folders: string[];
  folderFieldCounts?: Record<string, number>;
  onDeleteFolder: (folderKey: string) => void;
  onOpenFolder: (folderKey: string) => void;
  onCreateFolder: () => void;
  showFieldCount?: boolean;
  folderPrefix?: string;
  canCreate?: boolean;
  canDelete?: boolean;
}

export const FolderGrid: React.FC<FolderGridProps> = ({
  folders,
  folderFieldCounts = {},
  onOpenFolder,
  onDeleteFolder,
  onCreateFolder,
  showFieldCount = false,
  folderPrefix = "",
  canCreate = true,
  canDelete = true,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showFieldLimitModal, setShowFieldLimitModal] = useState(false);
  const permissions = usePermissions(user);
  const premiumTierUser = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const enterpriseTierUser = permissions.isAtLeastTier(PlanTier.ENTERPRISE);
  const largeBusinessTierUser = permissions.isAtLeastTier(
    PlanTier.LARGE_BUSINESS
  );

  const maxLimit = largeBusinessTierUser
    ? Infinity
    : enterpriseTierUser
    ? 10
    : premiumTierUser
    ? 5
    : 2;
  const countableFolders = folders.filter(
    (key) => !RESERVED_FOLDER_KEYS.includes(key)
  );
  const countableFoldersCount = countableFolders.length;

  const folderBenefits: PremiumBenefit[] = [
    {
      id: "folder-benefit-1",
      text: t("dashboard.upgrade.folders.benefit1") as string,
    },
    {
      id: "folder-benefit-2",
      text: t("dashboard.upgrade.folders.benefit2") as string,
    },
    {
      id: "folder-benefit-3",
      text: t("dashboard.upgrade.folders.benefit3") as string,
    },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          {folders?.length < 2
            ? t("dashboard.FolderGrid.yourFolder_one")
            : t("dashboard.FolderGrid.yourFolder_other", {
                count: folders?.length,
              })}
        </h3>
      </div>

      {!folders || folders.length === 0 ? (
        <SpaceEmptyState
          title={t("dashboard.FolderGrid.emptyTitle")}
          subtitle={t("dashboard.FolderGrid.emptySubtitle")}
          actionLabel={
            canCreate
              ? (t("dashboard.FolderGrid.createFirstFolder") as string)
              : undefined
          }
          onAction={canCreate ? onCreateFolder : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map((folderKey: string) => {
            const fullName = folderPrefix
              ? `${folderPrefix} ${folderKey}`
              : folderKey;
            const isReserved = RESERVED_FOLDER_KEYS.includes(folderKey);
            return (
              <div
                key={folderKey}
                className="group relative flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <HiFolderOpen size={20} />
                  </span>
                  {isReserved ? (
                    <FaLock
                      size={16}
                      className="relative z-10 mt-1 shrink-0 text-gray-400"
                      title={t("dashboard.FolderGrid.reservedFolder") as string}
                    />
                  ) : (
                    canDelete && (
                      <CustomButton
                        variant="none"
                        icon={<HiOutlineTrash size={18} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteFolder(folderKey);
                        }}
                        title=""
                        size="icon"
                        tooltip={t("common.delete") as string}
                        className="relative z-10 -mr-1 -mt-1 shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                      />
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onOpenFolder(folderKey)}
                  title={fullName}
                  className="mt-3 block text-left after:absolute after:inset-0 after:content-['']"
                >
                  <span className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                      <NoTranslate>{fullName}</NoTranslate>
                    </span>
                    <HiChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-colors group-hover:text-blue-700" />
                  </span>
                  {showFieldCount && (
                    <span className="mt-1 block text-xs text-gray-500">
                      {folderFieldCounts[folderKey] !== undefined
                        ? t("dashboard.FolderGrid.field", {
                            count: folderFieldCounts[folderKey],
                          })
                        : t("dashboard.FolderGrid.loading")}
                    </span>
                  )}
                </button>
              </div>
            );
          })}

          {canCreate &&
            (countableFoldersCount < maxLimit ? (
              <button
                onClick={onCreateFolder}
                className="group flex min-h-[128px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white p-4 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition-colors group-hover:border-blue-400 group-hover:text-blue-600">
                  <BiPlus size={20} />
                </span>
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
                  {largeBusinessTierUser
                    ? t("dashboard.FolderGrid.createNewFolder")
                    : t("dashboard.FolderGrid.foldersLimit", {
                        count: countableFoldersCount,
                        max: maxLimit,
                      })}
                </span>
              </button>
            ) : !largeBusinessTierUser ? (
              <button
                onClick={() => setShowFieldLimitModal(true)}
                className="group flex min-h-[128px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50 p-4 text-center transition-colors hover:border-blue-500"
              >
                <UnlockBadge />
                <span className="text-sm font-semibold text-blue-700">
                  {t("dashboard.FolderGrid.upgradeForMore")}
                </span>
                <span className="text-xs text-gray-600">
                  {t("dashboard.FolderGrid.getMoreFolders", { limit: 20 })}
                </span>
              </button>
            ) : null)}
        </div>
      )}

      <UpgradeModal
        showModal={showFieldLimitModal}
        setShowModal={setShowFieldLimitModal}
        title={t("dashboard.upgrade.folders.premiumTitle") as string}
        description={
          t("dashboard.upgrade.folders.premiumDescription") as string
        }
        benefits={folderBenefits}
        icon={HiFolderOpen}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </div>
  );
};
